var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { cloneElement, isValidElement, useCallback, useState, } from 'react';
import { useAsync } from './hooks/useAsync';
import { isDefaultConfig, loadDependencies, post, } from './utils';
const BKASH_BUTTON_ID = 'bKash_button';
export const BkashButton = ({ config, onClose, onSuccess, loader, children, renderError, debug = false, }) => {
    const [paymentID, setPaymentID] = useState('');
    const [bkashNotfoundError, setBkashNotFoundError] = useState(false);
    const [paymentAPIError, setPaymentAPIError] = useState(null);
    const [bkashButtonError, setBkashButtonError] = useState(null);
    //create a hidden button on dom with bkash button id
    const createBkashButton = useCallback(() => {
        const button = document.createElement('button');
        button.style.display = 'none';
        button.id = BKASH_BUTTON_ID;
        const body = document.querySelector('body');
        if (body) {
            body.appendChild(button);
            return;
        }
    }, []);
    //click on bkash button
    const triggerBkash = useCallback(() => {
        const createdButton = document.getElementById(BKASH_BUTTON_ID);
        if (createdButton) {
            createdButton.click();
            createdButton.remove();
            return;
        }
        setBkashButtonError(new Error('Could not find bkash button on document'));
    }, []);
    //bkash iframe config
    const initBkash = useCallback(() => {
        const { amount } = config;
        const bkashConfig = {
            paymentMode: 'checkout',
            paymentRequest: {
                amount: String(amount),
                intent: 'sale',
                currency: 'BDT',
            },
            createRequest: (request) => __awaiter(void 0, void 0, void 0, function* () {
                if (!isDefaultConfig(config)) {
                    try {
                        const result = yield config.onCreatePayment(request);
                        setPaymentID(result.paymentID);
                        window.bKash.create().onSuccess(result);
                    }
                    catch (error) {
                        setPaymentAPIError(error);
                        window.bKash.create().onError();
                    }
                    return;
                }
                const result = yield post(config.createPaymentURL, request, config.additionalHeaders || {});
                if (result.error !== null) {
                    window.bKash.create().onError();
                    setPaymentAPIError(result.error);
                    return;
                }
                setPaymentID(result.data.paymentID);
                window.bKash.create().onSuccess(result.data);
            }),
            executeRequestOnAuthorization: () => __awaiter(void 0, void 0, void 0, function* () {
                if (!isDefaultConfig(config)) {
                    try {
                        const result = yield config.onExecutePayment(paymentID);
                        onSuccess(result);
                    }
                    catch (error) {
                        setPaymentAPIError(error);
                        window.bKash.execute().onError();
                    }
                    return;
                }
                const result = yield post(config.executePaymentURL, { paymentID }, config.additionalHeaders || {});
                if (result.error !== null) {
                    setPaymentAPIError(result.error);
                    window.bKash.execute().onError();
                    return;
                }
                onSuccess(result.data);
            }),
            onClose: () => onClose(),
        };
        if (!window.bKash) {
            setBkashNotFoundError(true);
            return;
        }
        if (window.bKash) {
            window.bKash.init(bkashConfig);
        }
    }, [config, onClose, onSuccess, paymentID]);
    // Load dependencies & setup bkash
    const initFunction = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        if (debug) {
            console.log('[bKash] Initializing bKash');
        }
        createBkashButton();
        yield loadDependencies(config.bkashScriptURL);
        window.bKash = bKash; 
        initBkash();
        return true;
    }), [config.bkashScriptURL, createBkashButton, debug, initBkash]);
    const { loading, data, error } = useAsync(initFunction);
    /**
     * Handler that clicks bkash button and also calls children's onClick handler if it exists
     */
    const onClickHandler = useCallback((e) => {
        var _a, _b;
        triggerBkash();
        if (isValidElement(children)) {
            (_b = (_a = children.props).onClick) === null || _b === void 0 ? void 0 : _b.call(_a, e);
        }
    }, [children, triggerBkash]);
    if (loading) {
        return loader || React.createElement("p", null, "Loading...");
    }
    if (error) {
        if (renderError)
            return renderError(error);
        return (React.createElement("div", null,
            React.createElement("h6", null, "Bkash Loading Error"),
            React.createElement("p", null,
                "Error: ",
                error.message)));
    }
    if (bkashNotfoundError) {
        if (renderError)
            return renderError(new Error('Bkash was not found in window, please make sure you have provided the correct bkash.js  script url in	BkashComponent config prop'));
        return (React.createElement("p", null, "Bkash was not found in window, please make sure you have provided the correct bkash.js script url in BkashComponent config prop"));
    }
    if (paymentAPIError) {
        if (renderError)
            return renderError(paymentAPIError);
        return (React.createElement("div", null,
            React.createElement("h6", null, "There was a problem calling the payment API"),
            debug && React.createElement("p", null,
                "Error Message: ",
                paymentAPIError.message)));
    }
    if (bkashButtonError) {
        if (renderError)
            return renderError(bkashButtonError);
        return (React.createElement("div", null,
            React.createElement("h6", null, "There was a problem initiating a bKash create payment session"),
            debug && React.createElement("p", null,
                "Error Message: ",
                bkashButtonError.message)));
    }
    if (data) {
        return React.createElement("div", null, cloneElement(children, { onClick: onClickHandler }));
    }
    return null;
};
export default BkashButton;
export * from './hooks/useAsync';
export * from './utils';
//# sourceMappingURL=index.js.map